/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import classNames from "clsx";
import dayjs from "dayjs";

import { FaBaby } from "react-icons/fa";

import Modal, { ModalProps } from "react-bootstrap/Modal";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";

import { useApi } from "@/hooks/useApi";
import { useAttendance } from "@/hooks/useAttendance";
import { useSwal } from "@/hooks/useSwal";
import { usePartogramFetus } from "@/hooks/usePartogramFetus";
import { filterObject, filterObjectExcluding } from "@/utils/filterObject";
import { addFetus } from "@/utils/initialValues/addFetus";
import { transformData } from "@/utils/transformData";
import { productMap } from "@/utils/mappings/product";
import { examinerMap } from "@/utils/mappings/examiner";
import { AmnioticSac } from "@/enums/AmnioticSac";
import { FetusModalStatus } from "@/enums/FetusModalStatus";

import { ProfessionalsWithRoleContextProvider } from "@/contexts/ProfessionalWithRoleContext";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { AddFetusForm } from "../AddFetusForm";

type AddFetusRecord = typeof addFetus & {
	examiner: Examiner | null;
	product: Product | null;
};

type AddFetusModalProps = ModalProps & {
	tabFetuses?: PartogramFetus[] | null;
	updateTabFetusesList: () => Promise<void>;
	fillingHistory: {
		fetch: (pageToUse?: number) => Promise<void>;
		setIsLoading: (value: boolean) => void;
		openModal: () => void;
	};
};

export function AddFetusModal({
	tabFetuses,
	updateTabFetusesList,
	fillingHistory,
	...rest
}: AddFetusModalProps) {
	const { api } = useApi();
	const { attendance } = useAttendance();
	const { Toast } = useSwal();

	const { fetusModalStatus, currentPartogramDataId, setCurrentPartogramDataId } =
		usePartogramFetus();

	const isRegistering = fetusModalStatus === FetusModalStatus.Registering;
	const isViewing = fetusModalStatus === FetusModalStatus.Viewing;
	const isEditing = fetusModalStatus === FetusModalStatus.Editing;

	const [skeleton, setSkeleton] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const formik = useFormik({
		initialValues: addFetus,
		onSubmit() {
			handleSubmit();
		},
	});

	async function handleSubmit() {
		try {
			const attendanceId = attendance.id;

			if (!attendanceId) {
				Toast.fire({
					title: "Não foi possível identificar o atendimento",
					icon: "error",
				});

				return;
			}

			setIsLoading(true);

			const currentFetusId = currentTabFetusSelectedKey.split("-").at(-1);

			const filteredValues = filterObjectExcluding(formik.values, [
				"examiner_label",
				"product_label",
			]);

			const formattedValues = {
				...filteredValues,
				...(currentFetusId && { partogram_fetus_id: currentFetusId }),
			};

			if (isRegistering) {
				await api.post(`/partogram/${attendanceId}`, formattedValues);
			} else if (isEditing) {
				await api.put(`/partogram/${currentPartogramDataId}`, formattedValues);
			} else {
				Toast.fire({
					title: "Não foi possível identificar o tipo de ação",
					icon: "error",
				});

				return;
			}

			Toast.fire({
				title: "Partograma salvo com sucesso",
				icon: "success",
			});

			updateTabFetusesList();

			if (isRegistering && tabFetuses && tabFetuses.length) {
				fillingHistory.setIsLoading(true);
				fillingHistory.fetch();
			}

			onHide();
		} catch (error: any) {
			console.log(error);

			const [firstError] = Object.values<string>(error?.response?.data?.errors || {});
			Toast.fire({ title: firstError || "Erro ao salvar Partograma", icon: "error" });
		} finally {
			setIsLoading(false);
		}
	}

	const onHide = () => {
		if (isRegistering) {
			formik.resetForm();
		}

		rest.onHide?.();

		if (isViewing || isEditing) {
			setTimeout(fillingHistory.openModal, 15);
		}
	};

	const [currentTabFetusSelectedKey, setCurrentTabFetusSelectedKey] = useState("");

	useEffect(() => {
		if (tabFetuses && tabFetuses.length) {
			setCurrentTabFetusSelectedKey(
				`partogram-fetus-from-add-fetus-form-${tabFetuses[0].id}`
			);
		} else {
			setCurrentTabFetusSelectedKey("");
		}
	}, [tabFetuses]);

	const [isAmnioticSacRuptured, setIsAmnioticSacRuptured] = useState(false);

	async function checkAmnioticSacStatus() {
		try {
			const currentFetusId = currentTabFetusSelectedKey.split("-").at(-1);

			if (currentFetusId) {
				setSkeleton(true);

				const { data } = await api.get(
					`/partogram-fetuses/${currentFetusId}/amniotic-sac-status`
				);

				const { status } = data;

				setIsAmnioticSacRuptured(status === Number(AmnioticSac.Ruptured));
			}
		} catch (error: any) {
			console.log(error);

			Toast.fire({
				title: "Erro ao verificar a bolsa amniótica",
				icon: "error",
			});
		} finally {
			setSkeleton(false);
		}
	}

	useEffect(() => {
		formik.resetForm();
		checkAmnioticSacStatus();

		return () => {
			setIsAmnioticSacRuptured(false);
		};
	}, [currentTabFetusSelectedKey]);

	async function getPartogramFields() {
		try {
			if (currentPartogramDataId) {
				setSkeleton(true);

				const { data } = await api.get(`/partogram/${currentPartogramDataId}/fields`);

				const transformedData = transformData<AddFetusRecord>(data, {
					product: productMap,
					examiner: examinerMap,
				});

				transformedData.examiner_label = transformedData.examiner?.name || "";
				transformedData.product_label = transformedData.product?.name || "";
				transformedData.datetime = transformedData.datetime
					? dayjs(transformedData.datetime).format("YYYY-MM-DDTHH:mm")
					: "";

				const keysToIgnore = ["contractions_per_second"];
				const formattedData = Object.fromEntries(
					Object.entries(transformedData).map(([key, value]) => {
						if (value === null) {
							return [key, ""];
						}

						if (keysToIgnore.includes(key)) {
							return [key, value];
						}

						if (key === "medication_taken") {
							return [key, String(Number(value))];
						}

						return [key, String(value)];
					})
				);

				const addFetusInitialValuesKeys = Object.keys(addFetus);
				const filteredData = filterObject(formattedData, addFetusInitialValuesKeys);

				formik.setValues({ ...formik.values, ...filteredData });
			}
		} catch (error: any) {
			console.log(error);

			Toast.fire({
				title: "Erro ao verificar as informações do partograma",
				icon: "error",
			});
		} finally {
			setSkeleton(false);
		}
	}

	useEffect(() => {
		if (rest.show && (isViewing || isEditing)) {
			getPartogramFields();
		}
	}, [rest.show, fetusModalStatus]);

	useEffect(() => {
		if (isRegistering) {
			formik.resetForm();
			setCurrentPartogramDataId(0);
		}
	}, [fetusModalStatus]);

	return (
		<ProfessionalsWithRoleContextProvider roles={["Técnico de Enfermagem", "Enfermeiro"]}>
			<Modal size="lg" {...rest} onHide={onHide}>
				<Modal.Header closeButton>
					<span className="btn-close invisible" /> {/* this is to center modal title */}
					<Modal.Title>Partograma</Modal.Title>
				</Modal.Header>
				<Modal.Body className="px-0">
					{isRegistering ? (
						tabFetuses ? (
							tabFetuses.length ? (
								<Tabs
									unmountOnExit
									className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder px-10"
									activeKey={currentTabFetusSelectedKey}
									onSelect={(selectedKey) =>
										setCurrentTabFetusSelectedKey(selectedKey ?? "")
									}
								>
									{tabFetuses.map((fetus, index) => {
										const eventKey = `partogram-fetus-from-add-fetus-form-${fetus.id}`;
										const isActive = eventKey === currentTabFetusSelectedKey;

										return (
											<Tab
												key={eventKey}
												title={`Feto ${index + 1}`}
												eventKey={eventKey}
												className="mt-10"
												tabClassName={classNames(
													isActive && "text-active-primary"
												)}
											>
												<AddFetusForm
													formik={formik}
													skeleton={skeleton}
													isLoading={isLoading}
													onCancel={onHide}
													isAmnioticSacRuptured={isAmnioticSacRuptured}
												/>
											</Tab>
										);
									})}
								</Tabs>
							) : (
								<NoRecordsFeedback
									icon={<FaBaby />}
									message={`Nenhum feto foi encontrado. Clique em "Adicionar feto" para adicionar um novo feto.`}
								/>
							)
						) : (
							<AddFetusForm
								formik={formik}
								skeleton={skeleton}
								isLoading={isLoading}
								onCancel={onHide}
							/>
						)
					) : (
						<AddFetusForm
							formik={formik}
							skeleton={skeleton}
							isLoading={isLoading}
							onCancel={onHide}
						/>
					)}
				</Modal.Body>
			</Modal>
		</ProfessionalsWithRoleContextProvider>
	);
}
